// ─────────────────────────────────────────────────────────────────────────────
// FtueAnchorRegistryService
// ─────────────────────────────────────────────────────────────────────────────
// Maps anchor keys onto live DOM elements. Filled by the `appFtueAnchor`
// directive as templates mount, emptied as they leave.
//
// A key may be registered by more than one element at a time (the same concept
// exists on the order detail and inside the side-by-side panel). `resolve`
// returns the most recently registered *visible* one, so the tip lands on the
// surface the user is actually looking at.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { FtueAnchorKey } from './ftue-anchors';

@Injectable({ providedIn: 'root' })
export class FtueAnchorRegistryService {

  /** Emits the key that just appeared, so a waiting tip can retry at once. */
  readonly registered$: Observable<FtueAnchorKey>;

  /**
   * Emits the key whose element just left the DOM. An open card has to react:
   * its subject is gone, and a card pointing at nothing is worse than no card.
   */
  readonly unregistered$: Observable<FtueAnchorKey>;

  private readonly elements = new Map<FtueAnchorKey, HTMLElement[]>();
  private readonly registeredSubject = new Subject<FtueAnchorKey>();
  private readonly unregisteredSubject = new Subject<FtueAnchorKey>();

  constructor() {
    this.registered$ = this.registeredSubject.asObservable();
    this.unregistered$ = this.unregisteredSubject.asObservable();
  }

  // ── Registration ───────────────────────────────────────────────────────────

  register(key: FtueAnchorKey, element: HTMLElement): void {
    const existing = this.elements.get(key) ?? [];
    if (!existing.includes(element)) {
      this.elements.set(key, [...existing, element]);
    }
    this.registeredSubject.next(key);
  }

  unregister(key: FtueAnchorKey, element: HTMLElement): void {
    const candidates = this.elements.get(key) ?? [];
    if (!candidates.includes(element)) {
      return;
    }
    const remaining = candidates.filter((el) => el !== element);
    if (remaining.length) {
      this.elements.set(key, remaining);
    } else {
      this.elements.delete(key);
    }
    this.unregisteredSubject.next(key);
  }

  // ── Lookup ─────────────────────────────────────────────────────────────────

  /** The element a tip should point at, or `null` while none is on screen. */
  resolve(key: FtueAnchorKey): HTMLElement | null {
    const candidates = this.elements.get(key) ?? [];
    for (let i = candidates.length - 1; i >= 0; i--) {
      if (this.isVisible(candidates[i])) {
        return candidates[i];
      }
    }
    return null;
  }

  /** Used by the dev inspector to say whether a tip could fire on this page. */
  isResolvable(key: FtueAnchorKey | null): boolean {
    return key === null || this.resolve(key) !== null;
  }

  /**
   * "Can this be pointed at right now?"
   *
   * A zero-sized box covers most of it: `display: none` on the element or on any
   * ancestor collapses it, as does a container with no size, and a detached node
   * is caught by `isConnected` first.
   *
   * Two things it deliberately does NOT use:
   *
   *   • `offsetParent === null`, which this used to lead with. It is null for
   *     every `position: fixed` element whether or not that element is on
   *     screen, so a fixed anchor could never resolve and its tip would be
   *     abandoned on every attempt instead of shown. Nothing anchors to a fixed
   *     element today, but this registry is the primitive the tour is built on.
   *     The zero-box check already covers everything it was here for.
   *
   *   • the element's own computed `display`, which is redundant: a
   *     `display: none` element has no box, and one inside a hidden ancestor
   *     still reports its own `display` as whatever it specified.
   *
   * `visibility` has to be asked for separately: a hidden element keeps its
   * layout box, so the rect says nothing about it. It inherits, so this covers
   * an anchor hidden by an ancestor as well as one hidden directly.
   */
  private isVisible(element: HTMLElement): boolean {
    if (!element.isConnected) {
      return false;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return false;
    }
    return getComputedStyle(element).visibility !== 'hidden';
  }
}
